import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowRightLeft,
  Car,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
  Navigation,
  Play,
  Square,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import type { UIAccount } from '@/lib/use-accounts'
import { PullToRefresh } from '@/components/pull-to-refresh'
import { TimeAgo } from '@/components/time-ago'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAccounts } from '@/lib/use-accounts'
import { formatTimeAgo, getApiErrorMessage, localizeDigits } from '@/lib/utils'
import {
  useCancelRental,
  useContinueRental,
  useEndTrip,
  useExtendRental,
  useStartTrip,
  useTransferRental,
} from '@/lib/api'
import { useAuth } from '@/lib/auth-oidc'
import { useCountdown, useCountup } from '@/lib/use-countdown'
import { openDirections } from '@/components/rental-location-map'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// AccountStatus interface - using UIAccount from use-accounts

export type AccountStatus = UIAccount

export const Route = createFileRoute('/profile')({
  component: ProfileLayout,
})

// Countdown timer component
function Countdown({ targetDate }: { targetDate: Date | null | undefined }) {
  const countdown = useCountdown(targetDate)
  if (!countdown) return null
  return <>{countdown}</>
}

// Trip timer component - counts up from start time
function TripTimer({ startDate }: { startDate: Date | null | undefined }) {
  const elapsed = useCountup(startDate)
  if (!elapsed) return null
  return <>{elapsed}</>
}

function ProfileLayout() {
  const location = useLocation()

  // If we're on a child route (like /profile/1), render the outlet
  // Otherwise render the accounts list
  if (location.pathname !== '/profile') {
    return <Outlet />
  }

  return <AccountsPage />
}

function AccountsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth()
  const {
    accounts,
    isLoading: accountsLoading,
    refresh: refreshAccounts,
    dataUpdatedAt,
  } = useAccounts()

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (Rules of Hooks)
  // Mutation hooks
  const startTripMutation = useStartTrip()
  const endTripMutation = useEndTrip()
  const transferRentalMutation = useTransferRental()
  const continueRentalMutation = useContinueRental()
  const extendRentalMutation = useExtendRental()
  const cancelRentalMutation = useCancelRental()

  // State for dialogs
  const [actionDialog, setActionDialog] = useState<{
    open: boolean
    accountId: string
    accountName: string
    action: 'start-trip' | 'end-trip' | 'cancel-rental' | null
  }>({
    open: false,
    accountId: '',
    accountName: '',
    action: null,
  })
  const [endTripReserveDialog, setEndTripReserveDialog] = useState<{
    open: boolean
    fromAccountId: string
    fromAccountName: string
    tripCar?: string
  }>({
    open: false,
    fromAccountId: '',
    fromAccountName: '',
    tripCar: undefined,
  })
  const [continueRentalDialog, setContinueRentalDialog] = useState<{
    open: boolean
    fromAccountId: string
    fromAccountName: string
  }>({
    open: false,
    fromAccountId: '',
    fromAccountName: '',
  })
  const [moveReservationDialog, setMoveReservationDialog] = useState<{
    open: boolean
    fromAccountId: string
    fromAccountName: string
  }>({
    open: false,
    fromAccountId: '',
    fromAccountName: '',
  })

  // Combined loading state for mutations
  const isMutating =
    startTripMutation.isPending ||
    endTripMutation.isPending ||
    transferRentalMutation.isPending ||
    continueRentalMutation.isPending ||
    extendRentalMutation.isPending ||
    cancelRentalMutation.isPending

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      login()
    }
  }, [isAuthenticated, isAuthLoading, login])

  // CONDITIONAL RENDERING (after all hooks are called)
  // Show loading while checking authentication
  if (isAuthLoading) {
    return <LoadingSpinner variant="fullscreen" />
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null
  }

  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return localizeDigits(`${minutes}m`)
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return localizeDigits(mins > 0 ? `${hours}h ${mins}m` : `${hours}h`)
  }

  const handleStartTrip = (accountId: string, accountName: string) => {
    setActionDialog({
      open: true,
      accountId,
      accountName,
      action: 'start-trip',
    })
  }

  const handleEndTrip = (accountId: string, accountName: string) => {
    setActionDialog({
      open: true,
      accountId,
      accountName,
      action: 'end-trip',
    })
  }

  const handleCancelRental = (accountId: string, accountName: string) => {
    setActionDialog({
      open: true,
      accountId,
      accountName,
      action: 'cancel-rental',
    })
  }

  const handleEndTripWithReserve = (accountId: string, accountName: string) => {
    const account = accounts.find((a) => a.id === accountId)
    setEndTripReserveDialog({
      open: true,
      fromAccountId: accountId,
      fromAccountName: accountName,
      tripCar: account?.tripCar,
    })
  }

  const handleMoveReservation = (accountId: string, accountName: string) => {
    setMoveReservationDialog({
      open: true,
      fromAccountId: accountId,
      fromAccountName: accountName,
    })
  }

  const handleContinueRental = (accountId: string, accountName: string) => {
    setContinueRentalDialog({
      open: true,
      fromAccountId: accountId,
      fromAccountName: accountName,
    })
  }

  const handleExtendRental = async (accountId: string) => {
    try {
      await extendRentalMutation.mutateAsync({ accountName: accountId })
      toast.success(t('profile.accounts.rentalExtended'))
    } catch (error) {
      console.error('Failed to extend rental:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const confirmAction = async () => {
    const { accountId, action } = actionDialog
    const account = accounts.find((a) => a.id === accountId)
    if (!account) return

    try {
      if (action === 'start-trip') {
        await startTripMutation.mutateAsync({ accountName: accountId })
        toast.success(t('profile.accounts.tripStarted'))
      } else if (action === 'end-trip') {
        await endTripMutation.mutateAsync({ accountName: accountId })
        toast.success(t('profile.accounts.tripEnded'))
      } else if (action === 'cancel-rental') {
        await cancelRentalMutation.mutateAsync({ accountName: accountId })
        toast.success(t('profile.accounts.rentalCancelled'))
      }
      setActionDialog({
        open: false,
        accountId: '',
        accountName: '',
        action: null,
      })
    } catch (error) {
      console.error('Action failed:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const confirmEndTripAndReserve = async (toAccountId: string) => {
    const { fromAccountId } = endTripReserveDialog
    const toAccount = accounts.find((a) => a.id === toAccountId)

    if (!toAccount) return

    try {
      await endTripMutation.mutateAsync({
        accountName: fromAccountId,
        moveToAnotherAccount: toAccountId,
      })
      setEndTripReserveDialog({
        open: false,
        fromAccountId: '',
        fromAccountName: '',
        tripCar: undefined,
      })
      toast.success(t('profile.accounts.endTripReserveSuccess'))
    } catch (error) {
      console.error('End trip & reserve failed:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const confirmMoveReservation = async (toAccountId: string) => {
    const { fromAccountId } = moveReservationDialog
    const toAccount = accounts.find((a) => a.id === toAccountId)
    const fromAccount = accounts.find((a) => a.id === fromAccountId)

    if (!toAccount || !fromAccount) return

    try {
      await transferRentalMutation.mutateAsync({
        fromAccount: fromAccountId,
        toAccount: toAccountId,
      })
      setMoveReservationDialog({
        open: false,
        fromAccountId: '',
        fromAccountName: '',
      })
      toast.success(t('profile.accounts.moveReservationSuccess'))
    } catch (error) {
      console.error('Transfer rental failed:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const confirmContinueRental = async (toAccountId: string) => {
    const { fromAccountId } = continueRentalDialog

    try {
      await continueRentalMutation.mutateAsync({
        endOnAccount: fromAccountId,
        continueOnAccount: toAccountId,
      })
      setContinueRentalDialog({
        open: false,
        fromAccountId: '',
        fromAccountName: '',
      })
      toast.success(t('profile.accounts.continueRentalSuccess'))
    } catch (error) {
      console.error('Continue rental failed:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const getStatusBadge = (account: AccountStatus) => {
    if (account.status === 'ongoing-trip') {
      return (
        <Badge variant="default" className="bg-green-600">
          <Play className="h-3 w-3 me-1" />
          {t('profile.accounts.tripActive')}
        </Badge>
      )
    } else if (account.status === 'reserved') {
      return (
        <Badge variant="default" className="bg-blue-600">
          <Clock className="h-3 w-3 me-1" />
          {t('profile.accounts.reserved')}
        </Badge>
      )
    } else if (account.hasToken) {
      return (
        <Badge
          variant="outline"
          className="border-green-600 text-green-700 dark:text-green-400"
        >
          <CheckCircle2 className="h-3 w-3 me-1" />
          {t('profile.accounts.connected')}
        </Badge>
      )
    } else {
      return (
        <Badge
          variant="outline"
          className="border-red-600 text-red-700 dark:text-red-400"
        >
          <CheckCircle2 className="h-3 w-3 me-1" />
          {t('profile.accounts.disconnected')}
        </Badge>
      )
    }
  }

  return (
    <div className="fixed-fullscreen-safe fixed overflow-y-auto">
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">
                {t('profile.accounts.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('profile.accounts.description')}
              </p>
            </div>
          </div>
          {/* Data Age Indicator */}
          {dataUpdatedAt > 0 && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>
                <TimeAgo
                  date={new Date(dataUpdatedAt)}
                  prefix={t('common.updated')}
                />
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <PullToRefresh
        onRefresh={async () => {
          await refreshAccounts()
        }}
        className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-4"
      >
        <div className="space-y-3 pt-4">
          {accountsLoading && <LoadingSpinner size="sm" />}
          {!accountsLoading && accounts.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              {t('profile.accounts.noAccounts')}
            </div>
          )}
          {accounts.map((account) => (
            <Card
              key={account.id}
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => {
                navigate({
                  to: '/profile/$accountId',
                  params: { accountId: account.id },
                })
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-base">
                        {account.name}
                      </CardTitle>
                      {getStatusBadge(account)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {account.email}
                    </p>

                    {/* Status Details */}
                    <div className="space-y-1 text-xs">
                      {account.hasOngoingTrip && account.tripStartTime && (
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <Car className="h-3 w-3" />
                          <span>
                            {t('profile.accounts.trip')}: {account.tripCar}
                          </span>
                        </div>
                      )}
                      {account.hasUpcomingReservation &&
                        account.reservationEndTime &&
                        account.reservationEndTime instanceof Date && (
                          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                            <Clock className="h-3 w-3" />
                            <span>
                              {t('profile.accounts.reservation')}:{' '}
                              {account.reservationCar} •{' '}
                              {t('profile.accounts.expiresIn')}{' '}
                              <Countdown
                                targetDate={account.reservationEndTime}
                              />
                            </span>
                          </div>
                        )}
                      {account.status === 'idle' && account.lastTripEnd && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>
                            {t('profile.accounts.lastTrip')}:{' '}
                            {formatTimeAgo(account.lastTripEnd, t)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Trip Timer - shown prominently for ongoing trips */}
                  {account.hasOngoingTrip && account.tripStartTime ? (
                    <div className="flex flex-col items-end ms-3">
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400 font-mono tabular-nums">
                        <TripTimer startDate={account.tripStartTime} />
                      </span>
                    </div>
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ms-2" />
                  )}
                </div>

                {/* Action Buttons */}
                <div
                  className="flex flex-wrap gap-2 mt-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {account.status === 'reserved' && (
                    <>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() =>
                          handleStartTrip(account.id, account.name)
                        }
                        disabled={isMutating}
                      >
                        {startTripMutation.isPending ? (
                          <Loader2 className="h-3 w-3 me-1 animate-spin" />
                        ) : (
                          <Play className="h-3 w-3 me-1" />
                        )}
                        {t('profile.accounts.startTrip')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExtendRental(account.id)}
                        disabled={isMutating}
                      >
                        {extendRentalMutation.isPending ? (
                          <Loader2 className="h-3 w-3 me-1 animate-spin" />
                        ) : (
                          <Clock className="h-3 w-3 me-1" />
                        )}
                        {t('profile.accountDetails.extend')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          handleCancelRental(account.id, account.name)
                        }
                        disabled={isMutating}
                      >
                        {cancelRentalMutation.isPending ? (
                          <Loader2 className="h-3 w-3 me-1 animate-spin" />
                        ) : (
                          <X className="h-3 w-3 me-1" />
                        )}
                        {t('profile.accounts.cancelTrip')}
                      </Button>
                    </>
                  )}
                  {account.status === 'ongoing-trip' && (
                    <>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleEndTrip(account.id, account.name)}
                        disabled={isMutating}
                      >
                        {endTripMutation.isPending ? (
                          <Loader2 className="h-3 w-3 me-1 animate-spin" />
                        ) : (
                          <Square className="h-3 w-3 me-1" />
                        )}
                        {t('profile.accounts.endTrip')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleContinueRental(account.id, account.name)
                        }
                        disabled={isMutating}
                      >
                        {continueRentalMutation.isPending ? (
                          <Loader2 className="h-3 w-3 me-1 animate-spin" />
                        ) : (
                          <ArrowRightLeft className="h-3 w-3 me-1" />
                        )}
                        {t('profile.accounts.continueTrip')}
                      </Button>
                    </>
                  )}
                  {account.hasUpcomingReservation && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleMoveReservation(account.id, account.name)
                      }
                      disabled={isMutating}
                    >
                      {transferRentalMutation.isPending ? (
                        <Loader2 className="h-3 w-3 me-1 animate-spin" />
                      ) : (
                        <ArrowRightLeft className="h-3 w-3 me-1" />
                      )}
                      {t('profile.accounts.moveReservation')}
                    </Button>
                  )}
                  {account.vehicleLocation && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openDirections(
                          account.vehicleLocation!.latitude,
                          account.vehicleLocation!.longitude,
                        )
                      }
                    >
                      <Navigation className="h-3 w-3 me-1" />
                      {t('profile.accounts.navigate')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </PullToRefresh>

      {/* Start/End Trip Confirmation Dialog */}
      <AlertDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.action === 'start-trip'
                ? t('profile.accounts.startTripTitle')
                : actionDialog.action === 'end-trip'
                  ? t('profile.accounts.endTripTitle')
                  : actionDialog.action === 'cancel-rental'
                    ? t('profile.accounts.cancelTripTitle')
                    : t('settings.account.confirm')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.action === 'start-trip' && (
                <>
                  {t('profile.accounts.startTripDescription', {
                    name: actionDialog.accountName,
                  })}
                </>
              )}
              {actionDialog.action === 'end-trip' && (
                <>
                  {t('profile.accounts.endTripDescription', {
                    name: actionDialog.accountName,
                  })}
                </>
              )}
              {actionDialog.action === 'cancel-rental' && (
                <>
                  {t('profile.accounts.cancelTripDescription', {
                    name: actionDialog.accountName,
                  })}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
            {actionDialog.action === 'end-trip' ? (
              <>
                <AlertDialogAction
                  variant="outline"
                  onClick={() => {
                    setActionDialog({
                      open: false,
                      accountId: '',
                      accountName: '',
                      action: null,
                    })
                    handleEndTripWithReserve(
                      actionDialog.accountId,
                      actionDialog.accountName,
                    )
                  }}
                >
                  {t('profile.accounts.endReserve')}
                </AlertDialogAction>
                <AlertDialogAction onClick={confirmAction}>
                  {t('profile.accounts.endOnly')}
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction onClick={confirmAction}>
                {t('settings.account.confirm')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Reservation Dialog */}
      <AlertDialog
        open={moveReservationDialog.open}
        onOpenChange={(open) =>
          setMoveReservationDialog({ ...moveReservationDialog, open })
        }
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.moveReservationTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.moveReservationDescription', {
                name: moveReservationDialog.fromAccountName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-1">
            {accounts
              .filter((acc) => acc.id !== moveReservationDialog.fromAccountId)
              .map((account) => (
                <Card
                  key={account.id}
                  className="cursor-pointer hover:bg-accent transition-colors ring-1 ring-border"
                  onClick={() => confirmMoveReservation(account.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{account.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {account.email}
                        </p>
                      </div>
                      {account.status !== 'idle' && (
                        <Badge variant="outline" className="text-xs">
                          {account.status === 'ongoing-trip'
                            ? t('profile.accounts.tripActive')
                            : t('profile.accounts.reserved')}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Continue Rental Dialog */}
      <AlertDialog
        open={continueRentalDialog.open}
        onOpenChange={(open) =>
          setContinueRentalDialog({ ...continueRentalDialog, open })
        }
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.continueRentalTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.continueRentalDescription', {
                name: continueRentalDialog.fromAccountName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-1">
            {accounts
              .filter((acc) => acc.id !== continueRentalDialog.fromAccountId)
              .map((account) => (
                <Card
                  key={account.id}
                  className="cursor-pointer hover:bg-accent transition-colors ring-1 ring-border"
                  onClick={() => confirmContinueRental(account.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{account.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {account.email}
                        </p>
                      </div>
                      {account.status !== 'idle' && (
                        <Badge variant="outline" className="text-xs">
                          {account.status === 'ongoing-trip'
                            ? t('profile.accounts.tripActive')
                            : t('profile.accounts.reserved')}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Trip & Reserve Dialog */}
      <AlertDialog
        open={endTripReserveDialog.open}
        onOpenChange={(open) =>
          setEndTripReserveDialog({ ...endTripReserveDialog, open })
        }
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.endTripReserveTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.endTripReserveDescription', {
                name: endTripReserveDialog.fromAccountName,
                car: endTripReserveDialog.tripCar,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-1">
            {accounts.map((account) => (
              <Card
                key={account.id}
                className="cursor-pointer hover:bg-accent transition-colors ring-1 ring-border"
                onClick={() => confirmEndTripAndReserve(account.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{account.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {account.email}
                      </p>
                    </div>
                    {account.status !== 'idle' && (
                      <Badge variant="outline" className="text-xs">
                        {account.status === 'ongoing-trip'
                          ? t('profile.accounts.tripActive')
                          : t('profile.accounts.reserved')}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
